import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Footer from '../components/layout/Footer'
import Topbar from '../components/layout/Topbar'
import ProjectCard from '../components/project/ProjectCard'
import { fetchProjects } from '../services/api'
import type { PaginatedResponse, Project, ProjectFilters } from '../types'

export default function Projects() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 12,
    total: 0,
    total_pages: 0,
  })

  // Filters state
  const [filters, setFilters] = useState<ProjectFilters>({
    category: searchParams.get('category') as any ?? undefined,
    status: searchParams.get('status') as any ?? undefined,
    search: searchParams.get('search') ?? undefined,
    sort: searchParams.get('sort') as any ?? 'newest',
    page: parseInt(searchParams.get('page') ?? '1'),
  })

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response: PaginatedResponse<Project> = await fetchProjects(filters)
      setProjects(response.items ?? [])
      setPagination({
        page: response.page,
        page_size: response.page_size ?? 12,
        total: response.total,
        total_pages: response.total_pages ?? 1,
      })
    } catch (err) {
      console.error('Proje yukleme hatasi:', err)
      setError((err as Error).message ?? 'Projeler yuklenemedi')
      setProjects([]) // Hata durumunda bos array
    } finally {
      setLoading(false)
    }
  }, [filters])

  // Load projects
  useEffect(() => {
    void loadProjects()
  }, [loadProjects])

  // Update filters and URL params
  function updateFilters(newFilters: Partial<ProjectFilters>) {
    const updated = { ...filters, ...newFilters, page: 1 }
    setFilters(updated)

    // Update URL params
    const params = new URLSearchParams()
    if (updated.category) {params.set('category', updated.category)}
    if (updated.status) {params.set('status', updated.status)}
    if (updated.search) {params.set('search', updated.search)}
    if (updated.sort) {params.set('sort', updated.sort)}
    params.set('page', '1')
    setSearchParams(params)
  }

  // Change page
  function changePage(page: number) {
    const updated = { ...filters, page }
    setFilters(updated)

    const params = new URLSearchParams(searchParams)
    params.set('page', page.toString())
    setSearchParams(params)
  }


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>      <div style={{ flex: 1, overflow: 'auto' }}>
        <Topbar />
        <div style={{ flex: 1, overflow: 'auto', padding: '20px', backgroundColor: 'var(--bg-primary)' }}>
          {/* Header */}
          <div style={{ marginBottom: '30px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-primary)' }}>
              Projeler
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              {pagination.total} proje bulundu
            </p>
          </div>

          {/* Filters */}
          <div style={{
            display: 'flex',
            gap: '15px',
            marginBottom: '30px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {/* Search */}
            <input
              type="text"
              placeholder="Proje ara..."
              value={filters.search ?? ''}
              onChange={(e) => updateFilters({ search: e.target.value ?? undefined })}
              style={{
                flex: '1',
                minWidth: '250px',
                padding: '10px 15px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '14px',
              }}
            />

            {/* Category Filter */}
            <select
              value={filters.category ?? ''}
              onChange={(e) => updateFilters({ category: e.target.value as any ?? undefined })}
              style={{
                padding: '10px 15px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              <option value="">Tum Kategoriler</option>
              <option value="research">Arastirma</option>
              <option value="development">Gelistirme</option>
              <option value="design">Tasarim</option>
              <option value="data">Veri</option>
            </select>

            {/* Status Filter */}
            <select
              value={filters.status ?? ''}
              onChange={(e) => updateFilters({ status: e.target.value as any ?? undefined })}
              style={{
                padding: '10px 15px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              <option value="">Tum Durumlar</option>
              <option value="active">Aktif</option>
              <option value="completed">Tamamlandi</option>
              <option value="paused">Duraklatildi</option>
            </select>

            {/* Sort */}
            <select
              value={filters.sort ?? 'newest'}
              onChange={(e) => updateFilters({ sort: e.target.value as any })}
              style={{
                padding: '10px 15px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              <option value="newest">En Yeni</option>
              <option value="popular">En Populer</option>
              <option value="reward">En Yuksek Odul</option>
              <option value="deadline">Yakin Deadline</option>
            </select>

            {/* Create Project Button */}
            <button
              onClick={() => navigate('/create-project')}
              style={{
                padding: '10px 20px',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              + Yeni Proje
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
              Yukleniyor...
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: 'var(--error-color)',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '12px',
            }}>
              <p style={{ fontSize: '16px', marginBottom: '10px' }}>Hata: {error}</p>
              <button
                onClick={() => void loadProjects()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Tekrar Dene
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && projects.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '12px',
            }}>
              <p style={{ fontSize: '18px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                Proje bulunamadi
              </p>
              <p style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                Filtreleri degistirerek tekrar deneyin
              </p>
            </div>
          )}

          {/* Projects Grid */}
          {!loading && !error && projects.length > 0 && (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '20px',
                marginBottom: '30px',
              }}>
                {projects.map(project => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                  />
                ))}
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  marginTop: '30px',
                }}>
                  <button
                    onClick={() => changePage(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: pagination.page === 1 ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                      color: pagination.page === 1 ? 'var(--text-tertiary)' : 'var(--text-primary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Onceki
                  </button>

                  <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                    Sayfa {pagination.page} / {pagination.total_pages}
                  </span>

                  <button
                    onClick={() => changePage(pagination.page + 1)}
                    disabled={pagination.page === pagination.total_pages}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: pagination.page === pagination.total_pages ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                      color: pagination.page === pagination.total_pages ? 'var(--text-tertiary)' : 'var(--text-primary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      cursor: pagination.page === pagination.total_pages ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Sonraki
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <Footer />
      </div>
    </div>
  )
}
